import { FC, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import { Word as WordType } from 'models'
import { Face } from './face'
import { Reverse } from './reverse'
import { Card, MotionLi } from './styled'

type Props = {
  word: WordType
  blob: Blob | null
  isReversed: boolean
  isPrevious: boolean
  isInView: boolean
  i: number
  onReverse: () => void
  onClose: () => void
  onShare: () => void
}

export const WordCard: FC<Props> = ({
  word,
  blob,
  isReversed,
  isPrevious,
  isInView,
  onReverse,
  onClose,
  onShare,
  i,
}) => {
  const ref = useRef<HTMLLIElement>(null)
  const [boundingRect, setBoundingRect] = useState<DOMRect | null>(null)

  const watchScroll = () => {
    onClose()
  }

  useEffect(() => {
    if (isReversed && ref.current) {
      setBoundingRect(ref.current.getBoundingClientRect())
      document.addEventListener('scroll', watchScroll, { passive: true })
    } else if (!isReversed) {
      setBoundingRect(null)
      document.removeEventListener('scroll', watchScroll)
    }

    return () => {
      document.removeEventListener('scroll', watchScroll)
    }
  }, [isReversed])

  return (
    <MotionLi
      ref={ref}
      layout
      $isReversed={isReversed}
      $isPrevious={isPrevious}
      initial={{ scale: 0.6, opacity: 0 }}
      exit={{
        scale: 0.6,
        opacity: 0,
        transition: {
          type: 'tween',
          easings: ['circIn'],
          duration: 0.1,
        },
      }}
      animate={{
        scale: 1,
        opacity: 1,
        ...(boundingRect
          ? {
              translateY:
                window.innerHeight / 2 -
                boundingRect.top -
                boundingRect.height / 2,
              translateX:
                window.innerWidth / 2 -
                boundingRect.left -
                boundingRect.width / 2,
            }
          : {
              translateX: 0,
              translateY: 0,
            }),
        transition: {
          type: 'tween',
          easings: ['circIn'],
          duration: 0.3,
        },
      }}
      onAnimationComplete={() => {
        if (isReversed) {
          onShare()
        }
      }}
      key={word.tatar}
    >
      <motion.div
        animate={{
          transition: {
            type: 'tween',
            duration: 0.4,
            ease: 'easeOut',
            delay: i * 0.08,
          },
          transformStyle: 'preserve-3d',
          ...(isInView
            ? {
                translateY: 0,
                opacity: 1,
              }
            : {
                translateY: -100,
                opacity: 0,
              }),
        }}
      >
        <Card isReversed={isReversed}>
          <Face word={word} onClick={onReverse} />
          <Reverse word={word} blob={blob} />
        </Card>
      </motion.div>
    </MotionLi>
  )
}
